import MUIInput, { InputProps, inputClasses } from '@mui/material/Input';
import {
  FormControl,
  InputLabel,
  inputLabelClasses,
  styled,
  // InputLabelProps,
  // Theme,
} from '@mui/material';
import React, { useEffect } from 'react';
import key from 'weak-key';
import { useIMask } from 'react-imask';
import { IconName } from './icon';
import { InputHelperText } from './input-helper-text';
import { PasswordHints } from './input-password-hints';
import {
  BrandedInputAdornment,
  ClearInputAdornment,
  PasswordInputAdornment,
} from './adornment';
import theme from '../theme';

type InputVariant = 'filled' | 'unfilled' | 'password' | 'phone';
export interface CustomInputProps extends InputProps {
  variant?: InputVariant;
  iconName?: IconName;
  label?: string;
  helperText?: string;
  success?: boolean;
  min?: Number;
  warning?: boolean;
  info?: boolean;
  shrink?: boolean;
  showPasswordHints?: boolean;
  showEndAdornments?: boolean;
  labelColor?: string;
  defaultValue?: string;
  value?: string;
  localValue?: string;
  error?: boolean;
  // submitReady: (inputState: boolean) => void;
}

const BrandedFormControl = styled(FormControl)(() => ({
  borderRadius: '4px 4px 0 0',

  [`& .${inputLabelClasses.standard}.${inputLabelClasses.focused}`]: {
    color: theme.palette.cyan,
  },
  [`& .${inputClasses.underline}.${inputClasses.focused}:after`]: {
    borderColor: theme.palette.cyan,
  },
  [`&:hover .${inputClasses.underline}:before`]: {
    borderColor: theme.palette.cyan,
  },
}));

const BrandedInputLabel = styled(InputLabel)(() => ({
  '&.MuiInputLabel-shrink': {
    color: theme.palette.greyscale.lightGrey,
  },
}));

const BrandedPhoneInputLabel = styled(InputLabel)(() => ({
  marginLeft: '3.5rem',
  marginTop: '-1rem',
  '&.MuiInputLabel-shrink': {
    color: theme.palette.greyscale.lightGrey,
  },
}));

const BrandedInput = styled(MUIInput)(() => ({
  paddingTop: '0px',
  fontSize: '16px',
  color: theme.palette.greyscale.white,
}));

const PhoneInputContainer = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: stretch;
`;

export const PhoneInput: React.FC<CustomInputProps & InputProps> = ({
  value,
  placeholder,
  error,
  ...props
}) => {
  const { ref } = useIMask({ mask: '(000) 000-0000*' });

  return (
    <PhoneInputContainer>
      {/* Using a form control here prevents a MUI warning
      for multiple inputs inside the same form control */}
      <BrandedFormControl
        error={error}
        sx={{
          [`& .${inputClasses.underline}.${inputClasses.focused}:after`]: {
            borderColor: error ? theme.palette.error.main : theme.palette.cyan,
          },
        }}
      >
        <BrandedInput
          aria-labelledby="firstname"
          value="+1"
          className="country-phone-code"
          sx={{
            width: '3rem',
            marginRight: '1rem',
          }}
        />
      </BrandedFormControl>
      <BrandedInput id="input-custom" fullWidth inputRef={ref} {...props} />
    </PhoneInputContainer>
  );
};

const Input: React.FC<CustomInputProps & { children?: React.ReactNode }> = ({
  children,
  min,
  variant,
  iconName,
  label,
  helperText,
  disabled,
  error,
  success,
  warning,
  info,
  value,
  defaultValue,
  onChange,
  multiline,
  disableUnderline,
  shrink,
  type = 'text',
  showPasswordHints = 'true',
  showEndAdornments = true,
  labelColor = theme.palette.greyscale.white,
  ...props
}) => {
  const [pwdHidden, setPwdVisibility] = React.useState(true);
  const [localValue, setLocalValue] = React.useState(
    value || defaultValue || '',
  );

  useEffect(() => {
    if (value !== undefined) {
      setLocalValue(value || '');
    }
  }, [value]);

  const localOnChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setLocalValue(event.target.value);
    if (onChange) {
      onChange(event);
    }
  };

  const handleClear = () => {
    localOnChange({
      target: {
        value: '',
      },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const startAdornment = iconName ? (
    <BrandedInputAdornment iconName={iconName} position="start" />
  ) : null;

  const endAdornment =
    type === 'password' ? (
      <PasswordInputAdornment
        hidden={pwdHidden}
        toggleVisible={setPwdVisibility}
      />
    ) : (
      <ClearInputAdornment
        handleClear={handleClear}
        display={localValue?.length !== 0}
      />
    );
  const inputId = key({ label, variant, children });

  return (
    <BrandedFormControl
      error={error}
      variant="standard"
      color="secondary"
      fullWidth
      sx={{
        [`& .${inputClasses.underline}.${inputClasses.focused}:after`]: {
          borderColor: error ? theme.palette.error.main : theme.palette.cyan,
        },
        [`& .${inputLabelClasses.standard}.${inputLabelClasses.focused}`]: {
          color: error ? theme.palette.error.main : theme.palette.cyan,
        },
      }}
    >
      {label && variant !== 'phone' ? (
        <BrandedInputLabel
          shrink={shrink}
          htmlFor={inputId}
          sx={{ color: labelColor }}
        >
          {label}
        </BrandedInputLabel>
      ) : null}

      {label && variant === 'phone' ? (
        <BrandedPhoneInputLabel
          shrink={shrink}
          htmlFor={inputId}
          sx={{ color: labelColor }}
        >
          {label}
        </BrandedPhoneInputLabel>
      ) : null}

      {variant === 'phone' ? (
        <PhoneInput
          value={value}
          error={error}
          onChange={onChange}
          {...props}
        />
      ) : (
        <BrandedInput
          fullWidth
          id={inputId}
          startAdornment={startAdornment}
          endAdornment={showEndAdornments ? endAdornment : undefined}
          value={localValue}
          multiline={multiline}
          disableUnderline={disableUnderline}
          onChange={localOnChange}
          {...props}
          inputProps={{
            type: type === 'password' && !pwdHidden ? 'text' : type ?? 'text',
          }}
        />
      )}

      <InputHelperText
        error={error}
        success={success}
        warning={warning}
        info={info}
        text={helperText}
      />
      {(type === 'password' || variant === 'password') && showPasswordHints && (
        <PasswordHints password={value ?? ''} />
      )}
    </BrandedFormControl>
  );
};

export default Input;
